import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import invariant from 'tiny-invariant';
import { colorMap } from '../internal/color-map';
import { useSurface } from './surface-provider';
var asAllowlist = ['span', 'div', 'p'];
var fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";
var fontSizeMap = {
  '11px': css({
    fontSize: '11px'
  }),
  '12px': css({
    fontSize: '12px'
  }),
  '14px': css({
    fontSize: '14px'
  })
};
// NOTE: can't use numbers as keys or Constellation won't build. Weird one.
var fontWeightMap = {
  '400': css({
    fontWeight: 400
  }),
  '500': css({
    fontWeight: 500
  }),
  '700': css({
    fontWeight: 700
  })
};
var lineHeightMap = {
  '12px': css({
    lineHeight: '12px'
  }),
  '16px': css({
    lineHeight: '16px'
  }),
  '20px': css({
    lineHeight: '20px'
  }),
  '24px': css({
    lineHeight: '24px'
  }),
  '28px': css({
    lineHeight: '28px'
  }),
  '32px': css({
    lineHeight: '32px'
  }),
  '40px': css({
    lineHeight: '40px'
  })
};
var textAlignMap = {
  center: css({
    textAlign: 'center'
  }),
  end: css({
    textAlign: 'end'
  }),
  start: css({
    textAlign: 'start'
  })
};
var textTransformMap = {
  none: css({
    textTransform: 'none'
  }),
  lowercase: css({
    textTransform: 'lowercase'
  }),
  uppercase: css({
    textTransform: 'uppercase'
  })
};
var verticalAlignMap = {
  top: css({
    verticalAlign: 'top'
  }),
  middle: css({
    verticalAlign: 'middle'
  }),
  bottom: css({
    verticalAlign: 'bottom'
  })
};
var baseStyles = css({
  boxSizing: 'border-box',
  fontFamily: fontFamily
});
var truncateStyles = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});
/**
 * __Text__
 *
 * Text is a primitive component that has the Atlassian Design System's design guidelines baked in.
 * This includes considerations for text attributes such as color, font size, font weight, and line height.
 * It renders a `span` by default.
 *
 * @internal
 */

var Text = function Text(_ref) {
  var _ref$as = _ref.as,
      Component = _ref$as === void 0 ? 'span' : _ref$as,
      children = _ref.children,
      colorTuple = _ref.color,
      fontSize = _ref.fontSize,
      fontWeight = _ref.fontWeight,
      lineHeight = _ref.lineHeight,
      _ref$shouldTruncate = _ref.shouldTruncate,
      shouldTruncate = _ref$shouldTruncate === void 0 ? false : _ref$shouldTruncate,
      textAlign = _ref.textAlign,
      textTransform = _ref.textTransform,
      verticalAlign = _ref.verticalAlign,
      testId = _ref.testId,
      UNSAFE_style = _ref.UNSAFE_style;
  var surface = useSurface(); // @ts-ignore

  var _ref2 = colorTuple || [],
      _ref3 = _slicedToArray(_ref2, 2),
      _ref3$ = _ref3[0],
      color = _ref3$ === void 0 ? colorMap[surface] : _ref3$,
      fallback = _ref3[1];

  invariant(asAllowlist.includes(Component), "@atlaskit/ds-explorations: Text received an invalid \"as\" value of \"".concat(Component, "\""));
  return jsx(Component, {
    style: _objectSpread(_objectSpread({}, UNSAFE_style), fallback && {
      '--ds-co-fb': fallback
    }),
    css: [baseStyles, color && textColorMap[color], fontSize && fontSizeMap[fontSize], fontWeight && fontWeightMap[fontWeight], lineHeight && lineHeightMap[lineHeight], shouldTruncate && truncateStyles, textAlign && textAlignMap[textAlign], textTransform && textTransformMap[textTransform], verticalAlign && verticalAlignMap[verticalAlign]],
    "data-testid": testId
  }, children);
};

export default Text;
/**
 * THIS SECTION WAS CREATED VIA CODEGEN DO NOT MODIFY {@see http://go/af-codegen}
 * @codegen <<SignedSource::140ffff6e1310c1c37e2067e2c232b92>>
 * @codegenId colors
 * @codegenCommand yarn codegen-styles
 * @codegenParams ["text"]
 */

var textColorMap = {
  'color.text': css({
    color: "var(--ds-text, var(--ds-co-fb))"
  }),
  subtle: css({
    color: "var(--ds-text-subtle, var(--ds-co-fb))"
  }),
  subtlest: css({
    color: "var(--ds-text-subtlest, var(--ds-co-fb))"
  }),
  disabled: css({
    color: "var(--ds-text-disabled, var(--ds-co-fb))"
  }),
  inverse: css({
    color: "var(--ds-text-inverse, var(--ds-co-fb))"
  }),
  brand: css({
    color: "var(--ds-text-brand, var(--ds-co-fb))"
  }),
  selected: css({
    color: "var(--ds-text-selected, var(--ds-co-fb))"
  }),
  danger: css({
    color: "var(--ds-text-danger, var(--ds-co-fb))"
  }),
  warning: css({
    color: "var(--ds-text-warning, var(--ds-co-fb))"
  }),
  'warning.inverse': css({
    color: "var(--ds-text-warning-inverse, var(--ds-co-fb))"
  }),
  success: css({
    color: "var(--ds-text-success, var(--ds-co-fb))"
  }),
  discovery: css({
    color: "var(--ds-text-discovery, var(--ds-co-fb))"
  }),
  information: css({
    color: "var(--ds-text-information, var(--ds-co-fb))"
  })
};
/**
 * @codegenEnd
 */